import { NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'
import { cva } from 'class-variance-authority'

import { formatPercentage } from '@/domain/common/format'
import { Token } from '@/domain/types/Token'
import { InternalLink as InternalLinkType } from '@/routing/types'
import { InternalLink } from '@/ui/atoms/link/Link'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface NavigationItemProps {
  vaultToken: Token
  label: string
  apy: Percentage
  underlyingToken: Token
  underlyingTokenDeposit: NormalizedNumber
  active: boolean
  link: InternalLinkType
  variant: 'horizontal' | 'vertical'
}

export function NavigationItem({
  vaultToken,
  label,
  apy,
  underlyingToken,
  underlyingTokenDeposit,
  active,
  link,
  variant,
}: NavigationItemProps) {
  const transitionStyles = cn('transition-all duration-200 ease-in-out')

  return (
    <InternalLink
      variant="decorator"
      className={cn(navigationItemButtonVariants({ variant, active }), transitionStyles)}
      data-testid={testIds.savings.navigation.item}
      {...link}
    >
      <TokenIcon
        token={vaultToken}
        className={cn(navigationItemTokenIconVariants({ variant, active }), transitionStyles)}
      />
      <div
        className={cn(
          navigationItemTextContainerVariants({ variant, active, hasDeposit: underlyingTokenDeposit.gt(0) }),
          transitionStyles,
        )}
      >
        <div className="typography-label-3 text-primary" data-testid={testIds.savings.navigation.itemLabel}>
          {label}
        </div>
        {underlyingTokenDeposit.gt(0) && (
          <div className="typography-label-4 text-secondary" data-testid={testIds.savings.navigation.itemBalance}>
            {underlyingToken.formatUSD(underlyingTokenDeposit, { compact: true })}
          </div>
        )}
      </div>
      <div className={cn(navigationItemApyVariant({ variant, active }), transitionStyles)}>
        {formatPercentage(apy, { minimumFractionDigits: 2 })}
      </div>
    </InternalLink>
  )
}

const navigationItemButtonVariants = cva(
  cn(
    'relative flex shrink-0 rounded-[10px] border border-primary bg-white shadow-xs',
    'hover:border-secondary hover:shadow-sm',
    'focus-visible:bg-primary focus-visible:text-neutral-950',
    'focus-visible:outline-hidden focus-visible:ring-3 focus-visible:ring-primary-200 focus-visible:ring-offset-0',
  ),
  {
    variants: {
      variant: {
        horizontal: 'mr-2 size-28',
        vertical: 'mb-2 h-16 w-full',
      },
      active: {
        true: 'border-savings-600 hover:border-savings-500',
      },
    },
    compoundVariants: [
      {
        variant: 'vertical',
        active: true,
        className: 'h-28 w-full',
      },
      {
        variant: 'horizontal',
        active: true,
        className: 'w-36',
      },
    ],
  },
)

const navigationItemTokenIconVariants = cva('absolute top-1/2 size-6 translate-x-4', {
  variants: {
    variant: {
      horizontal: '-translate-y-10',
      vertical: '-translate-y-1/2',
    },
    active: {
      true: 'size-8',
    },
  },
  compoundVariants: [
    {
      variant: 'vertical',
      active: true,
      className: '-translate-y-10',
    },
  ],
})

const navigationItemTextContainerVariants = cva('absolute top-1/2 flex flex-col items-start gap-0.5', {
  variants: {
    variant: {
      horizontal: 'translate-x-4',
      vertical: '-translate-y-1/2 translate-x-[52px]',
    },
    hasDeposit: {
      true: 'translate-y-2',
      false: 'translate-y-6',
    },
    active: { true: undefined }, // needed for compound variants
  },
  compoundVariants: [
    {
      variant: 'vertical',
      active: false,
      className: '-translate-y-1/2',
    },
    {
      variant: 'vertical',
      active: true,
      className: 'translate-x-4',
    },
    {
      variant: 'vertical',
      active: true,
      hasDeposit: true,
      className: 'translate-y-2',
    },
    {
      variant: 'vertical',
      active: true,
      hasDeposit: false,
      className: 'translate-y-6',
    },
  ],
})

const navigationItemApyVariant = cva('-translate-x-4 typography-label-4 absolute right-0 origin-right text-secondary', {
  variants: {
    variant: {
      horizontal: 'top-3.5',
      vertical: '-translate-y-1/2 top-1/2',
    },
    active: {
      true: '',
    },
  },
  compoundVariants: [
    {
      variant: 'vertical',
      active: true,
      className: 'translate-y-[25px]',
    },
  ],
})
